import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquare, X, Send, Check, RefreshCw, CheckCircle2, ShieldCheck } from "lucide-react";
import { chatbotConfig, scoreLead, AGE_RANGES } from "../config/chatbot";
import { submitLead } from "../lib/submit";
import { LANGS } from "../i18n";
import { PLANS } from "../data/plans";

const SEGMENTS = ["digital_nomad", "seasonal_worker", "trader_conference", "relocation", "family", "executive_hnw", "partner_agency"];
const CYCLE_IDS = ["A", "B", "C", "D", "E"];
const NEEDS = ["smartbag", "telemedicine", "connectivity", "risk_options"];

// Ordered intake steps. type: quick | multi | text | number | yesno | lang
const STEPS = [
  { key: "language", type: "lang", prompt: "askLanguage" },
  { key: "customer_segment", type: "quick", prompt: "askSegment", opts: (t) => SEGMENTS.map((s) => ({ value: s, label: t(`quote.segments.${s}`) })) },
  { key: "country_of_residence", type: "text", prompt: "askResidence" },
  { key: "current_country", type: "text", prompt: "askCurrent" },
  { key: "next_destination", type: "text", prompt: "askNext" },
  { key: "countries_next_12_months", type: "text", prompt: "askCountries", list: true },
  { key: "traveler_count", type: "number", prompt: "askTravelers" },
  { key: "age_ranges", type: "multi", prompt: "askAge", opts: () => AGE_RANGES.map((a) => ({ value: a, label: a })) },
  { key: "mobility_cycle", type: "quick", prompt: "askCycle", opts: (t) => CYCLE_IDS.map((id) => ({ value: id, label: t(`data.cycles.${id}.label`) })) },
  { key: "plan_interest", type: "quick", prompt: "askPlan", opts: (t) => PLANS.map((p) => ({ value: p.id, label: t(`data.plans.${p.id}.name`) })) },
  { key: "needs", type: "multi", prompt: "askNeeds", opts: (t) => NEEDS.map((n) => ({ value: n, label: t(`quote.needs.${n}`) })) },
  { key: "pre_existing_disclosure", type: "yesno", prompt: "askPreExisting" },
  { key: "payment_preference", type: "quick", prompt: "askPayment", opts: (t) => [{ value: "annual", label: t("chatbot.annual") }, { value: "monthly", label: t("chatbot.monthly") }] },
  { key: "name", type: "text", prompt: "askName" },
  { key: "email", type: "text", prompt: "askEmail" },
  { key: "whatsapp", type: "text", prompt: "askWhatsapp" },
];

export function ChatbotWidget() {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [multi, setMulti] = useState([]);
  const [result, setResult] = useState(null);
  const scrollRef = useRef(null);

  // Typebot mode (only when configured + not demo)
  const useTypebot = chatbotConfig.enabled && !chatbotConfig.demoMode && chatbotConfig.typebotId && chatbotConfig.typebotUrl;

  const start = () => {
    setMsgs([{ from: "bot", text: t("chatbot.intro") }, { from: "bot", text: t(`chatbot.${STEPS[0].prompt}`) }]);
    setIdx(0);
    setAnswers({});
    setResult(null);
    setMulti([]);
    setText("");
  };

  useEffect(() => {
    if (open && msgs.length === 0 && !useTypebot) start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [msgs, result]);

  const step = STEPS[idx];

  const advance = (displayValue, storedValue) => {
    const newAnswers = { ...answers, [step.key]: storedValue };
    setAnswers(newAnswers);
    const nextMsgs = [...msgs, { from: "user", text: displayValue }];
    const nextIdx = idx + 1;
    if (nextIdx < STEPS.length) {
      nextMsgs.push({ from: "bot", text: i18n.t(`chatbot.${STEPS[nextIdx].prompt}`) });
      setMsgs(nextMsgs);
      setIdx(nextIdx);
      setText("");
      setMulti([]);
    } else {
      setMsgs(nextMsgs);
      finish(newAnswers);
    }
  };

  const finish = (a) => {
    const needs = a.needs || [];
    const payload = {
      source: "r0am_chatbot",
      brand: "R0AM by Wadiah Mutual",
      lead_type: "chatbot_quote_intake",
      language: a.language || i18n.language,
      customer_segment: a.customer_segment || "",
      mobility_cycle: a.mobility_cycle || "",
      country_of_residence: a.country_of_residence || "",
      current_country: a.current_country || "",
      next_destination: a.next_destination || "",
      countries_next_12_months: a.countries_next_12_months || [],
      traveler_count: a.traveler_count || 0,
      age_ranges: a.age_ranges || [],
      plan_interest: a.plan_interest || "",
      smartbag_interest: needs.includes("smartbag"),
      telemedicine_interest: needs.includes("telemedicine"),
      connectivity_interest: needs.includes("connectivity"),
      risk_options_interest: needs.includes("risk_options"),
      pre_existing_disclosure: a.pre_existing_disclosure === true,
      payment_preference: a.payment_preference || "",
      contact: { name: a.name || "", email: a.email || "", whatsapp: a.whatsapp || "" },
      compliance_acknowledged: true,
    };
    payload.routing = scoreLead(payload);
    // Demo mode: logs payload; live mode POSTs to mutualautomation (see lib/submit).
    submitLead("chatbotQuoteIntake", payload, { label: "[R0AM chatbot demo]" });
    setResult(payload.routing);
  };

  const submitText = () => {
    const v = text.trim();
    if (!v) return;
    if (step.key === "email" && !/^\S+@\S+\.\S+$/.test(v)) return;
    if (step.type === "number") {
      const n = parseInt(v) || 0;
      advance(String(n), n);
    } else if (step.list) {
      const arr = v.split(",").map((x) => x.trim()).filter(Boolean);
      advance(arr.join(", "), arr);
    } else {
      advance(v, v);
    }
  };

  const toggleMulti = (val) => setMulti((m) => (m.includes(val) ? m.filter((x) => x !== val) : [...m, val]));
  const confirmMulti = () => {
    if (multi.length === 0) return;
    const labels = step.opts(t).filter((o) => multi.includes(o.value)).map((o) => o.label).join(", ");
    advance(labels, multi);
  };

  const launcherBottom = "bottom-24 md:bottom-24";

  return (
    <>
      {/* Launcher */}
      <button
        onClick={() => setOpen((v) => !v)}
        className={`fixed end-4 ${launcherBottom} z-40 flex items-center justify-center w-14 h-14 rounded-full bg-signal hover:bg-[#0047d6] shadow-[0_8px_30px_rgba(0,85,255,0.45)] transition-all hover:scale-105`}
        aria-label={t("chatbot.launcher")}
        data-testid="chatbot-launcher"
      >
        {open ? <X size={24} className="text-white" /> : <MessageSquare size={24} className="text-white" />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.25 }}
            className={`fixed end-4 ${launcherBottom} mb-16 z-40 w-[calc(100vw-2rem)] sm:w-[400px] h-[560px] max-h-[75vh] rounded-2xl border border-white/10 bg-navy-800 shadow-2xl flex flex-col overflow-hidden`}
            data-testid="chatbot-panel"
          >
            {/* Header */}
            <div className="flex items-center gap-3 p-4 border-b border-white/10 bg-navy-900">
              <div className="w-9 h-9 rounded-full bg-signal/15 grid place-items-center"><MessageSquare size={18} className="text-signal" /></div>
              <div className="flex-1">
                <p className="font-display font-semibold text-sm text-white">{t("chatbot.title")}</p>
                <p className="text-[11px] text-health flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-health" /> {t("chatbot.online")}</p>
              </div>
              <button onClick={start} aria-label={t("chatbot.restart")} data-testid="chatbot-restart" className="text-white/50 hover:text-white p-1"><RefreshCw size={16} /></button>
            </div>

            {useTypebot ? (
              <iframe title="R0AM Mobility Assistant" src={chatbotConfig.typebotUrl} className="flex-1 w-full border-0" data-testid="chatbot-typebot-frame" />
            ) : (
              <>
                {/* Messages */}
                <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chatbot-messages">
                  {msgs.map((m, i) => (
                    <div key={i} className={`flex ${m.from === "user" ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[82%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${m.from === "user" ? "bg-signal text-white rounded-ee-sm" : "bg-navy-700 text-white/85 rounded-es-sm"}`}>
                        {m.text}
                      </div>
                    </div>
                  ))}

                  {result && (
                    <div className="space-y-3" data-testid="chatbot-result">
                      <div className="flex justify-start">
                        <div className="max-w-[90%] px-3.5 py-3 rounded-2xl rounded-es-sm bg-navy-700">
                          <div className="flex items-center gap-2 text-health"><CheckCircle2 size={16} /><span className="font-semibold text-sm">{t("chatbot.doneTitle")}</span></div>
                          <p className="mt-1.5 text-sm text-white/75">{t("chatbot.doneBody")}</p>
                          {result.requires_assisted_review && <p className="mt-2 text-xs text-gold" data-testid="chatbot-assisted">{t("chatbot.assistedNote")}</p>}
                          {result.high_value_lead && !result.requires_assisted_review && <p className="mt-2 text-xs text-signal" data-testid="chatbot-highvalue">{t("chatbot.highValueNote")}</p>}
                          <p className="mt-2 text-[10px] text-white/30">{t("chatbot.demoNote")}</p>
                        </div>
                      </div>
                      <div className="px-1">
                        <p className="text-xs text-white/50 mb-2">{t("chatbot.fallback")}</p>
                        <Link to="/contact" onClick={() => setOpen(false)} className="inline-flex items-center justify-center w-full py-2.5 rounded-md bg-cta hover:bg-cta-dark text-white text-sm font-semibold transition-colors" data-testid="chatbot-fallback-cta">
                          {t("chatbot.fallbackCta")}
                        </Link>
                      </div>
                    </div>
                  )}
                </div>

                {/* Input area */}
                {!result && step && (
                  <div className="p-3 border-t border-white/10 bg-navy-900" data-testid="chatbot-input-area">
                    {step.type === "lang" && (
                      <div className="flex flex-wrap gap-2">
                        {LANGS.map((l) => (
                          <button key={l.code} onClick={async () => { await i18n.changeLanguage(l.code); advance(l.label, l.code); }} data-testid={`chatbot-lang-${l.code}`} className="px-3 py-2 rounded-md bg-navy-700 border border-white/10 text-sm text-white/80 hover:border-signal transition-colors">
                            {l.flag} {l.label}
                          </button>
                        ))}
                      </div>
                    )}

                    {(step.type === "quick" || step.type === "yesno") && (
                      <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                        {(step.type === "yesno" ? [{ value: true, label: t("chatbot.yes") }, { value: false, label: t("chatbot.no") }] : step.opts(t)).map((o) => (
                          <button key={String(o.value)} onClick={() => advance(o.label, o.value)} data-testid={`chatbot-opt-${o.value}`} className="px-3 py-2 rounded-md bg-navy-700 border border-white/10 text-sm text-white/80 hover:border-signal transition-colors">
                            {o.label}
                          </button>
                        ))}
                      </div>
                    )}

                    {step.type === "multi" && (
                      <div>
                        <div className="flex flex-wrap gap-2 max-h-28 overflow-y-auto">
                          {step.opts(t).map((o) => (
                            <button key={String(o.value)} onClick={() => toggleMulti(o.value)} data-testid={`chatbot-multi-${o.value}`} className={`px-3 py-2 rounded-md text-sm border transition-colors ${multi.includes(o.value) ? "bg-signal/20 border-signal text-white" : "bg-navy-700 border-white/10 text-white/80 hover:border-white/30"}`}>
                              {multi.includes(o.value) && <Check size={12} className="inline me-1" />}{o.label}
                            </button>
                          ))}
                        </div>
                        <button onClick={confirmMulti} disabled={multi.length === 0} data-testid="chatbot-multi-confirm" className="mt-2 w-full py-2 rounded-md bg-cta hover:bg-cta-dark disabled:opacity-40 text-white text-sm font-semibold transition-colors">{t("chatbot.next")}</button>
                      </div>
                    )}

                    {(step.type === "text" || step.type === "number") && (
                      <div className="flex gap-2">
                        <input
                          type={step.type === "number" ? "number" : "text"}
                          value={text}
                          onChange={(e) => setText(e.target.value)}
                          onKeyDown={(e) => e.key === "Enter" && submitText()}
                          placeholder={t("chatbot.typePlaceholder")}
                          className="flex-1 bg-navy-700 border border-white/10 rounded-md px-3.5 py-2.5 text-sm text-white placeholder:text-white/35 focus:outline-none focus:border-signal"
                          data-testid="chatbot-text-input"
                          autoFocus
                        />
                        <button onClick={submitText} className="px-3.5 rounded-md bg-signal hover:bg-[#0047d6] transition-colors" aria-label={t("chatbot.next")} data-testid="chatbot-send"><Send size={16} className="text-white" /></button>
                      </div>
                    )}
                  </div>
                )}

                {/* Compliance footer */}
                <div className="px-3 py-2 border-t border-white/10 bg-navy-900 flex items-start gap-1.5" data-testid="chatbot-compliance">
                  <ShieldCheck size={12} className="text-health shrink-0 mt-0.5" />
                  <p className="text-[9px] leading-snug text-white/35">{t("chatbot.compliance")}</p>
                </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export default ChatbotWidget;
